import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Calendar,
  Users,
  Briefcase,
  Package,
  DollarSign,
  FileText,
  Settings,
  LogOut,
  X,
  TrendingUp,
  Truck,
  Clock,
  ShoppingCart,
  Rocket,
  Megaphone,
  Image,
  CreditCard,
  Bot,
  HelpCircle,
  Shield,
  MessageSquare,
  Lock,
  ChevronDown,
  FileCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cleanLogout } from '@/lib/auth';
import { useAuth } from '@/hooks/useAuth';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useProviderFeatures } from '@/hooks/useFeatureAccess';
import { useSubscription } from '@/hooks/useSubscription';
import { PremiumModal } from '@/components/subscription/PremiumModal';

interface ProviderSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: LucideIcon;
  labelKey: string;
  path: string;
  tutorialId: string;
  feature?: string;
  premium?: boolean;
}

interface MenuGroup {
  labelKey: string;
  items: MenuItem[];
}

const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  pos: 'Punto de Venta (TPV)',
  time_clock: 'Control Horario',
  suppliers: 'Gestión de Proveedores',
};

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  pos: 'Cobra y gestiona ventas directamente desde tu negocio con el sistema de punto de venta integrado.',
  time_clock: 'Controla los fichajes de entrada y salida de tu equipo con el reloj de fichar digital.',
  suppliers: 'Gestiona tus proveedores, pedidos de compra e inventario de forma centralizada.',
};

const menuGroups: MenuGroup[] = [
  {
    labelKey: 'sidebar.groupManagement',
    items: [
      { icon: LayoutDashboard, labelKey: 'sidebar.dashboard', path: '/dashboard', tutorialId: 'dashboard' },
      { icon: Calendar, labelKey: 'sidebar.calendar', path: '/calendar', tutorialId: 'calendar' },
      { icon: Briefcase, labelKey: 'sidebar.services', path: '/services', tutorialId: 'services' },
      { icon: Users, labelKey: 'sidebar.workers', path: '/workers', tutorialId: 'workers' },
    ],
  },
  {
    labelKey: 'sidebar.groupClients',
    items: [
      { icon: Users, labelKey: 'sidebar.clients', path: '/clients', tutorialId: 'clients' },
      { icon: Clock, labelKey: 'sidebar.timeClock', path: '/time-clock', feature: 'time_clock', tutorialId: 'time-clock' },
    ],
  },
  {
    labelKey: 'sidebar.groupFinance',
    items: [
      { icon: ShoppingCart, labelKey: 'sidebar.sales', path: '/sales', feature: 'pos', tutorialId: 'sales' },
      { icon: DollarSign, labelKey: 'sidebar.payments', path: '/payments', tutorialId: 'payments' },
      { icon: FileText, labelKey: 'sidebar.invoices', path: '/invoices', tutorialId: 'invoices' },
      { icon: Package, labelKey: 'sidebar.inventory', path: '/inventory', tutorialId: 'inventory' },
      { icon: Truck, labelKey: 'sidebar.suppliers', path: '/suppliers', feature: 'suppliers', tutorialId: 'suppliers' },
    ],
  },
  {
    labelKey: 'sidebar.groupMarketing',
    items: [
      { icon: Megaphone, labelKey: 'sidebar.marketing', path: '/marketing', tutorialId: 'marketing' },
      { icon: Rocket, labelKey: 'sidebar.aiCampaigns', path: '/ai-campaigns', tutorialId: 'ai-campaigns' },
      { icon: Image, labelKey: 'sidebar.portfolio', path: '/portfolio', tutorialId: 'portfolio' },
      { icon: MessageSquare, labelKey: 'sidebar.candidateMessages', path: '/candidate-messages', tutorialId: 'candidate-messages' },
    ],
  },
  {
    labelKey: 'sidebar.groupAIConfig',
    items: [
      { icon: Bot, labelKey: 'sidebar.aiAgents', path: '/ai-agents', tutorialId: 'ai-agents' },
      { icon: TrendingUp, labelKey: 'sidebar.analytics', path: '/analytics', tutorialId: 'analytics' },
      { icon: Settings, labelKey: 'sidebar.settings', path: '/settings', tutorialId: 'settings' },
      { icon: FileCheck, labelKey: 'sidebar.terms', path: '/configuracion/terminos', tutorialId: 'terms' },
      { icon: CreditCard, labelKey: 'sidebar.subscription', path: '/settings/subscription', tutorialId: 'subscription' },
      { icon: HelpCircle, labelKey: 'sidebar.support', path: '/support', tutorialId: 'support' },
    ],
  },
];

// Fallback group labels when translation keys are missing
const GROUP_LABELS: Record<string, string> = {
  'sidebar.groupManagement': 'Gestión',
  'sidebar.groupClients': 'Clientes',
  'sidebar.groupFinance': 'Finanzas',
  'sidebar.groupMarketing': 'Marketing',
  'sidebar.groupAIConfig': 'IA y Config',
};

export function ProviderSidebar({ isOpen, onClose }: ProviderSidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasAdminAccess } = useAdminRole();
  const { features } = useProviderFeatures();
  const { plan } = useSubscription();
  const providerId = user?.id;

  const [premiumModal, setPremiumModal] = useState<{ open: boolean; feature: string }>({
    open: false,
    feature: '',
  });

  // Determine which groups start open (those with an active item)
  const getInitialOpenGroups = () => {
    const open: Record<string, boolean> = {};
    menuGroups.forEach((group) => {
      const hasActiveItem = group.items.some(
        (item) => location.pathname === item.path
      );
      open[group.labelKey] = hasActiveItem;
    });
    // Always open the first group by default
    if (menuGroups.length > 0 && !Object.values(open).some(Boolean)) {
      open[menuGroups[0].labelKey] = true;
    }
    return open;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(getInitialOpenGroups);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const accessibleFeatures = new Set(features.map((f) => f.key));

  // Fetch provider profile for sidebar header
  const { data: profile } = useQuery({
    queryKey: ['sidebar-profile', providerId],
    queryFn: async () => {
      if (!providerId) return null;
      const { data } = await supabase
        .from('profiles')
        .select('business_name, full_name, avatar_url')
        .eq('id', providerId)
        .single();
      return data;
    },
    enabled: !!providerId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: lowStockCount } = useQuery({
    queryKey: ['low-stock-count', providerId],
    queryFn: async () => {
      if (!providerId) return 0;
      const { data, error } = await supabase
        .from('inventory')
        .select('id, quantity, min_quantity')
        .eq('provider_id', providerId)
        .eq('status', 'active');
      if (error) return 0;
      return (data || []).filter(item => item.quantity <= item.min_quantity).length;
    },
    enabled: !!providerId,
    refetchInterval: 60000,
  });

  const { data: unreadMessagesCount } = useQuery({
    queryKey: ['unread-candidate-messages', providerId],
    queryFn: async () => {
      if (!providerId) return 0;
      const { count, error } = await supabase
        .from('candidate_messages')
        .select('id', { count: 'exact', head: true })
        .eq('provider_id', providerId)
        .eq('sender_type', 'candidate')
        .is('read_at', null);
      if (error) return 0;
      return count || 0;
    },
    enabled: !!providerId,
    refetchInterval: 30000,
  });

  const queryClient = useQueryClient();

  const handleLogout = async () => {
    await cleanLogout(queryClient);
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || '';
  const businessName = profile?.business_name || '';
  const avatarUrl = profile?.avatar_url;
  const initials = displayName
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const planName = plan?.name || 'Free';

  const renderMenuItem = (item: MenuItem) => {
    const isActive = location.pathname === item.path;
    const showInventoryBadge = item.path === '/inventory' && lowStockCount && lowStockCount > 0;
    const showMessagesBadge = item.path === '/candidate-messages' && unreadMessagesCount && unreadMessagesCount > 0;
    const badgeCount = showInventoryBadge ? lowStockCount : showMessagesBadge ? unreadMessagesCount : 0;
    const isLocked = !!item.feature && !accessibleFeatures.has(item.feature);

    if (isLocked) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={() => {
            setPremiumModal({ open: true, feature: item.feature! });
            onClose();
          }}
          className="w-full flex items-center gap-2.5 pl-8 pr-3 py-2 rounded-lg transition-all duration-200 text-[13px] text-gray-400 hover:text-gray-500 hover:bg-gray-50"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          <item.icon className="h-4 w-4 flex-shrink-0 text-gray-300" />
          <span className="flex-1 text-left">{t(item.labelKey)}</span>
          <Lock className="h-3 w-3 text-gray-300 flex-shrink-0" />
        </button>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        data-tutorial={item.tutorialId}
        onClick={(e) => {
          if (isActive) e.preventDefault();
          onClose();
        }}
        className={`
          flex items-center gap-2.5 pl-8 pr-3 py-2 rounded-lg transition-all duration-200 relative text-[13px]
          ${
            isActive
              ? 'text-[#B8860B] font-medium'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }
        `}
        style={{
          fontFamily: "'Outfit', sans-serif",
          backgroundColor: isActive ? 'rgba(184,134,11,0.08)' : undefined,
        }}
      >
        <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[#B8860B]' : 'text-gray-400'}`} />
        <span className="flex-1">{t(item.labelKey)}</span>
        {(showInventoryBadge || showMessagesBadge) && (
          <Badge className={`ml-auto text-white font-bold animate-pulse text-[10px] px-1.5 py-0 ${showMessagesBadge ? 'bg-red-500 hover:bg-red-600' : 'bg-[#B8860B] hover:bg-[#996F0A]'}`}>
            {badgeCount}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 xl:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'}
        `}
        style={{ borderRight: '1px solid rgba(184,134,11,0.06)' }}
      >
        <div className="flex flex-col h-screen overflow-hidden">
          {/* Logo header */}
          <div className="px-5 pt-6 pb-4">
            <Link
              to="/dashboard"
              className="block hover:opacity-80 transition-opacity"
              onClick={() => onClose()}
            >
              <h1
                className="font-heading text-xl text-[#2B2520] tracking-tight"
                style={{ fontFamily: "'Libre Baskerville', serif" }}
              >
                BeautyBook<span className="text-[#B8860B]">AI</span>
              </h1>
              {businessName && (
                <p
                  className="text-[11px] text-gray-400 mt-0.5 truncate"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {businessName}
                </p>
              )}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden absolute top-4 right-3 text-gray-400 hover:text-[#B8860B] hover:bg-gray-50 h-8 w-8"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Separator */}
          <div className="mx-4 border-t" style={{ borderColor: 'rgba(184,134,11,0.08)' }} />

          {/* Navigation groups */}
          <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-1">
            {menuGroups.map((group) => {
              const isGroupOpen = openGroups[group.labelKey] ?? false;
              const groupLabel = t(group.labelKey, GROUP_LABELS[group.labelKey]);

              return (
                <div key={group.labelKey}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.labelKey)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 transition-colors duration-150 group"
                  >
                    <span
                      className="text-[11.5px] font-semibold uppercase tracking-wider text-gray-400 group-hover:text-gray-500 transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {groupLabel}
                    </span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-gray-300 transition-transform duration-200 ${isGroupOpen ? 'rotate-0' : '-rotate-90'}`}
                    />
                  </button>
                  {isGroupOpen && (
                    <div className="mt-0.5 mb-1 space-y-0.5">
                      {group.items.map(renderMenuItem)}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Admin link + logout */}
          <div className="px-3 pb-2 space-y-0.5">
            {hasAdminAccess && (
              <Link
                to="/admin"
                onClick={onClose}
                className={`
                  flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 text-[13px]
                  ${location.pathname.startsWith('/admin')
                    ? 'text-[#B8860B] font-medium'
                    : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                  }
                `}
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  backgroundColor: location.pathname.startsWith('/admin') ? 'rgba(184,134,11,0.08)' : undefined,
                }}
              >
                <Shield className="h-4 w-4" />
                <span>Panel Admin</span>
              </Link>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-gray-400 hover:text-gray-600 hover:bg-gray-50 py-2 px-3 rounded-lg transition-all duration-200 text-[13px] h-auto"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2.5 text-gray-300" />
              {t('sidebar.logout')}
            </Button>
          </div>

          {/* User avatar footer */}
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ borderTop: '1px solid rgba(184,134,11,0.06)' }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold"
                style={{
                  background: 'linear-gradient(135deg, #B8860B, #D4AF37)',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p
                className="text-[13px] text-gray-700 font-medium truncate"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {displayName}
              </p>
              <p
                className="text-[10px] text-gray-400 truncate"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {planName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Premium feature modal */}
      <PremiumModal
        open={premiumModal.open}
        onClose={() => setPremiumModal({ open: false, feature: '' })}
        featureName={FEATURE_DISPLAY_NAMES[premiumModal.feature] || premiumModal.feature}
        featureDescription={FEATURE_DESCRIPTIONS[premiumModal.feature]}
      />
    </>
  );
}
